<script setup lang="ts">
import { useRecentPages } from '@/utils/recentPages'

definePage({
  meta: {
    id: 'acceuil',
    icon: 'mdi-account-cowboy-hat-outline',
    title: 'Accueil',
    drawerIndex: 0,
    requiresAuth: false
  },
})

const storeU = useUtilisateurStore()
const estChargé = computed(() => storeU.estChargé())

const { smAndDown } = useDisplay()
const router = useRouter()
const recentPages = useRecentPages()

// Only show pages the user currently has access to (access may have changed
// since they were last visited, or the route may no longer exist).
const recentPagesVisibles = computed(() =>
  recentPages.value.filter((page) => {
    try {
      const resolved = router.resolve(page.path)
      return resolved.matched.length > 0 && storeU.aAccès(resolved.meta)
    } catch {
      return false
    }
  }),
)

const weatherRef = ref<{ heroGradient: string } | null>(null)
const pageBg = computed(
  () =>
    weatherRef.value?.heroGradient ??
    'linear-gradient(165deg, #3b7fc4 0%, #5a9bd8 45%, #8fbfe8 100%)',
)
</script>

<template>
  <div class="accueil-page" :style="estChargé ? { background: pageBg } : undefined">
    <template v-if="estChargé">
      <!-- Quick access to recently visited pages (mobile only) -->
      <div
        v-if="smAndDown && recentPagesVisibles.length"
        class="recent-pages px-4 pt-3"
      >
        <div class="text-caption font-weight-medium text-white mb-2 px-1">
          Pages récentes
        </div>
        <div class="recent-pages-row">
          <v-card
            v-for="page in recentPagesVisibles"
            :key="page.path"
            :to="page.path"
            class="recent-page-card"
            rounded="lg"
            variant="elevated"
          >
            <v-icon :icon="page.icon" size="26" color="primary" />
            <span class="recent-page-title">{{ page.title }}</span>
          </v-card>
        </div>
      </div>

      <!-- Full-width hero -->
      <CurrentWeather ref="weatherRef" hero />

      <!-- Rest of the page over the same background -->
      <div class="page-content px-4 px-md-6 pt-2 pb-8">
        <!-- Upcoming semis widget -->
        <div v-if="storeU.siteAAccès('semis')" style="margin-bottom: 24px;">
          <UpcomingSemisWidget />
        </div>

        <!-- Upcoming récoltes widget -->
        <!-- 
        <div v-if="storeU.siteAAccès('semis')" style="margin-bottom: 24px;">
          <UpcomingRecoltesWidget />
        </div> -->

        <WeatherForecasts12h />

        <!-- Plan de ferme map widget (radar animation) -->
        <PlanDeFermeWidget v-if="storeU.siteAAccès('prisme.base')" class="mt-4" />
      </div>
    </template>

    <div v-else class="empty-state">
      <v-icon icon="custom:wizard-onion" size="5em" color="secondary" class="mb-4" />
      <div class="text-h6 font-weight-medium">Bienvenue</div>
      <div class="text-body-2 text-medium-emphasis">
        Sélectionnez un site pour afficher la météo.
      </div>
    </div>
  </div>
</template>

<style scoped>
.accueil-page {
  min-height: 100%;
  transition: background 0.4s ease;
}

.page-content {
  max-width: 1180px;
  margin: 0 auto;
}

/* ── Recent pages quick access (mobile) ───── */
.recent-pages-row {
  display: flex;
  gap: 10px;
  overflow-x: auto;
  padding-bottom: 4px;
  scrollbar-width: none;
  -webkit-overflow-scrolling: touch;
}

.recent-pages-row::-webkit-scrollbar {
  display: none;
}

.recent-page-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  flex: 0 0 auto;
  width: 88px;
  height: 88px;
  padding: 8px;
  text-align: center;
}

.recent-page-title {
  font-size: 0.7rem;
  line-height: 1.1;
  font-weight: 600;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  min-height: 60vh;
  gap: 4px;
}
</style>
